import React, { useState, useEffect } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { clipboardService } from '../services/api';
import toast from 'react-hot-toast';

const VerticalVoteButtons = ({ itemId, initialVotes = 0, onVoteChange }) => {
  const [userVote, setUserVote] = useState(null); // 'upvote', 'downvote', or null
  const [votes, setVotes] = useState(initialVotes);
  const [loading, setLoading] = useState(false);
  const [endpointsAvailable, setEndpointsAvailable] = useState(true);

  // Fetch user's current vote when component mounts
  useEffect(() => {
    const fetchUserVote = async () => {
      try {
        const response = await clipboardService.getUserVote(itemId);
        setUserVote(response.vote);
      } catch (error) {
        console.error('Error fetching user vote:', error);
        console.error('Error response:', error.response?.data);
        // Don't show error toast for this - it's expected if user is not logged in or endpoints don't exist yet
        if (error.response?.status === 404) {
          setEndpointsAvailable(false);
        }
      }
    };

    fetchUserVote();
  }, [itemId]);

  const handleUpvote = async () => {
    if (loading) return;
    
    setLoading(true);
    try {
      const response = await clipboardService.upvoteItem(itemId);
      
      // Update local state based on API response
      if (response.action === 'upvoted') {
        setUserVote('upvote');
        setVotes(prev => prev + 1);
      } else if (response.action === 'removed') {
        setUserVote(null);
        setVotes(prev => prev - 1);
      } else if (response.action === 'changed') {
        setUserVote('upvote');
        setVotes(prev => prev + 2); // +2 because we're changing from downvote to upvote
      }
      
      // Notify parent component
      if (onVoteChange) {
        onVoteChange(itemId, response.action, votes);
      }
      
      toast.success(response.message);
    } catch (error) {
      console.error('Error upvoting:', error);
      console.error('Error response:', error.response?.data);
      if (error.response?.status === 404) {
        toast.error('Voting endpoints not yet implemented on server');
      } else {
        toast.error('Failed to upvote item');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownvote = async () => {
    if (loading) return;
    
    setLoading(true);
    try {
      const response = await clipboardService.downvoteItem(itemId);
      
      // Update local state based on API response
      if (response.action === 'downvoted') {
        setUserVote('downvote');
        setVotes(prev => prev - 1);
      } else if (response.action === 'removed') {
        setUserVote(null);
        setVotes(prev => prev + 1);
      } else if (response.action === 'changed') {
        setUserVote('downvote');
        setVotes(prev => prev - 2); // -2 because we're changing from upvote to downvote
      }
      
      // Notify parent component
      if (onVoteChange) {
        onVoteChange(itemId, response.action, votes);
      }
      
      toast.success(response.message);
    } catch (error) {
      console.error('Error downvoting:', error);
      console.error('Error response:', error.response?.data);
      if (error.response?.status === 404) {
        toast.error('Voting endpoints not yet implemented on server');
      } else {
        toast.error('Failed to downvote item');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center space-y-1 min-w-[2rem]">
      {/* Upvote button */}
      <button
        type="button"
        onClick={handleUpvote}
        disabled={loading || !endpointsAvailable}
        className={`p-1 rounded transition-colors duration-200 ${
          userVote === 'upvote'
            ? 'text-green-600 bg-green-50 hover:bg-green-100'
            : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
        } ${loading || !endpointsAvailable ? 'opacity-50 cursor-not-allowed' : ''}`}
        title={!endpointsAvailable ? 'Voting not yet available' : 'Upvote'}
      >
        <ChevronUp className={`h-9 w-9 ${loading ? 'animate-pulse' : ''}`} />
      </button>
      
      {/* Vote count */}
      <span className={`text-sm font-medium min-w-[2rem] text-center ${
        votes > 0 ? 'text-green-600' : votes < 0 ? 'text-red-600' : 'text-gray-700'
      }`}>
        {votes}
      </span>
      
      {/* Downvote button */}
      <button
        type="button"
        onClick={handleDownvote}
        disabled={loading || !endpointsAvailable}
        className={`p-1 rounded transition-colors duration-200 ${
          userVote === 'downvote'
            ? 'text-red-600 bg-red-50 hover:bg-red-100'
            : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
        } ${loading || !endpointsAvailable ? 'opacity-50 cursor-not-allowed' : ''}`}
        title={!endpointsAvailable ? 'Voting not yet available' : 'Downvote'}
      >
        <ChevronDown className={`h-9 w-9 ${loading ? 'animate-pulse' : ''}`} />
      </button>
    </div>
  );
};

export default VerticalVoteButtons; 